'use client'

import { useEffect, useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import { motion } from '@/components/ui/motion-provider'
import { ChevronUp } from 'lucide-react'
import { Button } from './button'
import { cn } from '@/lib/utils'

interface ScrollToTopProps {
  className?: string
  threshold?: number
}

export function ScrollToTop({ className, threshold = 400 }: ScrollToTopProps) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > threshold) {
        setIsVisible(true)
      } else {
        setIsVisible(false)
      }
    }

    window.addEventListener('scroll', toggleVisibility)
    return () => window.removeEventListener('scroll', toggleVisibility)
  }, [threshold])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={cn(
            'fixed bottom-6 right-6 z-50',
            className
          )}
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ 
            opacity: 1, 
            scale: 1,
            y: [0, -8, 0],
            transition: {
              y: {
                repeat: Infinity,
                duration: 2,
                ease: "easeInOut"
              }
            }
          }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          whileHover={{ scale: 1.1 }}
        >
          <Button
            size="icon"
            variant="ghost"
            onClick={scrollToTop}
            className="rounded-full backdrop-blur-sm bg-background/30 hover:bg-background/40 border border-border/50 shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <ChevronUp className="h-5 w-5 text-foreground" />
            <span className="sr-only">Scroll to top</span>
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
